import React, { forwardRef } from "react";
import { View, Image, StyleSheet, ScrollView, Platform, } from "react-native";
import { Text } from "react-native-paper";
import { CustomIcon } from "../../../components/CustomIcon";
import { useThemeSettings } from "../../../ThemeProvider";
import { useTranslation } from "react-i18next";
import { useUserStore } from "../../../store/userStore";
import { GradientStrokeBox } from "../../../components/GradientStrokeBox";
import { ARTICLE_HEIGHT, FULL_WIDTH } from "../../../shared/constants/dimension";
import CustomLinearGradient, { GRADIENT_TYPE } from "../../../components/CustomLinearGradient";
import { Article } from "../../../shared/types/DataTypes";
import { useActiveTabStore } from "../../../store/activeTabStore";
import { ROUTES } from "../../../shared/constants/routes";
import ThinkOLogo from "../../../components/ThinkoLogo";
import { getImageUri } from "../../../shared/helpers/utils";
type Props = {
  articles: Article[];
  comment: string;
  look: GRADIENT_TYPE;
  collectionName?: string;
};

const SharePreviewCard = ({ articles, comment, look, collectionName }: Props) => {
  const { theme } = useThemeSettings();
  const { t } = useTranslation();
  const { user } = useUserStore();
  const activetab = useActiveTabStore((state) => state.activetab);
  const renderImageLayout = () => {
    if (!articles || articles.length === 0) return null;

    /** CASE 1: One article → full image */
    if (articles.length === 1) {
      const a = articles[0];
      return (
        <View style={styles.topSection}>
          <Image source={getImageUri(a?.image!)} style={styles.image} />
          <CustomLinearGradient gradientType={GRADIENT_TYPE.ARTICLE_CARD} style={{ top: 80 }}>
            <View style={styles.overlay}>
              <Text variant={"headlineSmall"} style={{ color: theme.colors.white, lineHeight: 20 }} numberOfLines={4}>
                {a.title}
              </Text>
            </View>
          </CustomLinearGradient>
        </View>
      );
    }

    /** CASE 2: 2 or 3 articles → grid */
    if (articles.length > 1) {
      return (
        <View style={styles.multiContainer}>

          {/* FIRST ROW – two images */}
          <View style={styles.row}>
            {articles.slice(0, 2).map((a) => (
              <View key={a.id} style={[styles.smallCard, {height:ARTICLE_HEIGHT}]}>
                <Image source={getImageUri(a?.image!)} style={styles.smallImage} />
                <CustomLinearGradient gradientType={GRADIENT_TYPE.ARTICLE_CARD} style={{ top: 80 }}>
                  <View style={styles.overlay}>
                    <Text variant={"headlineSmall"} style={{ color: theme.colors.white, lineHeight: 20 }} numberOfLines={4}>
                      {a.title}
                    </Text>
                  </View>
                </CustomLinearGradient>
              </View>
            ))}
          </View>

          {/* SECOND ROW – full width image (only if 3 items) */}
          {articles[2] && (
            <View style={[styles.largeCard, {height:ARTICLE_HEIGHT}]}>
              <Image source={getImageUri(articles[2]?.image!)} style={styles.largeImage} />
              <CustomLinearGradient gradientType={GRADIENT_TYPE.ARTICLE_CARD} style={{ top: 80 }}>
                  <View style={styles.overlay}>
                    <Text variant={"headlineSmall"} style={{ color: theme.colors.white, lineHeight: 20 }} numberOfLines={4}>
                      {articles[2].title}
                    </Text>
                  </View>
                </CustomLinearGradient>
            </View>
          )}
        </View>
      );
    }
  };
  const getShareNote = () => {
    const tabSuffix =
      activetab === ROUTES.LISTEN ? 'Listen'
      : activetab === ROUTES.WATCH ? 'Watch'
      : '';
    return t(`screens.share.readFullNews${collectionName ? 'Collection' : ''}${tabSuffix}`);
  }
  const getChildren = () => {
    const child = <View>

      {/* Quote */}
      {comment ?
        <View style={{ marginVertical: 12 }}>
          <GradientStrokeBox wrapperStyle={{ width: FULL_WIDTH, borderRadius: 12 }} style={{ padding: 12 }}>
            {user?.thinkoUser?.picture ?
              <Image
                source={user?.thinkoUser?.picture}
                style={styles.avatar}
              /> : <View style={styles.avatar}>
                <CustomIcon icon="user" color={theme.colors.primary} size={50} />
              </View>}

            <View style={{ flex: 1 }}>
              <Text style={[styles.user, { color: theme?.colors?.primary }]}>{user?.thinkoUser?.name || user?.firebaseUser?.providerData[0]?.displayName || '@explorer'}</Text>
              <Text style={styles.quote}>
                "{comment}"
              </Text>
            </View>
          </GradientStrokeBox>
        </View>
        : null}
      <View style={[styles.topSection]}>
        {collectionName && <View style={styles.cornerTag}>
          <Text style={styles.cornerText}>{collectionName}</Text>
        </View>}
        {renderImageLayout()}

        <View>
          {articles.length == 1 ? <Text style={[styles.bodyText, { color: theme.colors.textPrimary }]}>
            {articles[0]?.gistContent}
          </Text> : null}

          {/* Bottom Link */}
          <View style={styles.bottomRow}>
            <ThinkOLogo size={20} />
            <Text style={[styles.bottomText, { color: theme?.colors?.primary }]}>{getShareNote()}</Text>
          </View>
        </View>
      </View>
      {/* Body */}
    </View>;
    return <CustomLinearGradient gradientType={look} angle={180}>{child}</CustomLinearGradient>
  }
  return getChildren();
};

export default SharePreviewCard;

const styles = StyleSheet.create({
  card: {
    marginHorizontal: 18,
    marginTop: 24,
    borderRadius: 16,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "rgba(203, 198, 198, 0.3)",

  },

  topSection: {
    position: "relative",
  },

  cornerTag: {
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 2,
    borderTopLeftRadius: 16,
    backgroundColor: "#fff",
    borderBottomRightRadius: 6,
  },

  cornerText: {
    color: "#7A3C3C",
    fontSize: 16,
    margin: 5,
    fontWeight: "400",
  },

  image: {
    width: "100%",
    height: 190,
  },

  overlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 12,
    paddingVertical: 10,
    maxHeight: 100,
  },

  headline: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },

  bodyText: {
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 12,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 12,
  },

  user: {
    color: "#7A3C3C",
    fontWeight: "700",
    marginBottom: 4,
  },

  quote: {
    fontStyle: "italic",
  },

  bottomRow: {
    flexDirection: "row",
    alignItems: "center",
    alignContent: "center",
    justifyContent: "center",
  },

  bottomText: {
    color: "#7A3C3C",
    marginLeft: 8,
  },
  multiContainer: {
    width: "100%",
  },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 6,
  },

  smallCard: {
    flex: 1,
    borderRadius: 16,
    overflow: "hidden",
    marginBottom: 6,
    position: "relative",
    borderWidth: 2,
    borderColor: "rgba(14, 36, 15, 0.15)",
  },

  smallImage: {
    width: "100%",
    height: "100%",
    borderRadius: 16,
  },

  overlaySmall: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: 8,
    backgroundColor: "rgba(0,0,0,0.45)",
  },

  smallHeadline: {
    color: "#fff",
    fontSize: 12.5,
    fontWeight: "600",
  },


  largeCard: {
    width: "100%",
    borderRadius: 16,
    overflow: "hidden",
    position: "relative",
    borderWidth: 2,
    borderColor: "rgba(14, 36, 15, 0.15)",
  },

  largeImage: {
    width: "100%",
    height: "100%",
  },
});